import { Formik, Form, Field, ErrorMessage } from "formik";
import { KeenIcon, LazyFormikSelect } from "@/components";
import { toAbsoluteUrl } from "@/utils";

const EmailSendModal = ({
    title = 'Send Email',
    open,
    onClose,
    onSubmit,
    loading,
    initialValues,
    validationSchema,
    hideCC = false,
    showInvoiceTemplate = false,
}) => {
    const emailTemplates = [
        { id: 1, label: "Template 1", image: "admin-invoice-two.png" },
        { id: 2, label: "Template 2", image: "admin-invoice-three.png" },
        { id: 3, label: "Template 3", image: "admin-invoice-four.png" },
    ];

    if (!open) return null;

    return (
        <div className="modal-wrapper modal-animate-fade">
            <div className="modal-body w-[85%] max-h-[90vh] max-w-xl modal-animate-scale">

                <h3 className="capitalize card-title">{title}</h3>
                <button
                    type="button"
                    onClick={onClose}
                    className="modal-close-btn"
                >
                    <KeenIcon icon="cross" />
                </button>

                <Formik
                    initialValues={
                        showInvoiceTemplate
                            ? {
                                showInvoiceTemplate: 1,
                                ...initialValues,
                            }
                            : initialValues
                    }
                    validationSchema={validationSchema}
                    onSubmit={(values, formikHelpers) => {
                        const payload = { ...values };

                        if (showInvoiceTemplate) {
                            payload.showInvoiceTemplate = Number(values.showInvoiceTemplate ?? 1);
                        } else {
                            delete payload.showInvoiceTemplate;
                        }

                        return onSubmit(payload, formikHelpers);
                    }}
                >
                    {({ values, errors, touched, setFieldValue }) => (
                        <Form className="mt-3">

                            {/* EMAIL */}
                            <div className="mb-3">
                                <label className="form-label mb-1">
                                    Email <span className="text-red-600">*</span>
                                </label>
                                <Field
                                    type="email"
                                    name="email"
                                    placeholder="Enter email"
                                    className="input"
                                />
                                <ErrorMessage
                                    name="email"
                                    component="div"
                                    className="mt-1 text-xs text-red-500"
                                />
                            </div>

                            {/* CC */}
                            {!hideCC && (
                                <div className="mb-3">
                                    <label className="form-label mb-1">CC</label>
                                    <LazyFormikSelect
                                        name="cc_emails"
                                        isMulti={true}
                                        creatable={true}
                                        options={[]}
                                        placeholder="Add CC emails"
                                    />
                                    {errors.cc_emails && touched.cc_emails && (
                                        <div className="mt-1 text-xs text-red-500">
                                            {[...new Set(errors.cc_emails)].join(", ")}
                                        </div>
                                    )}

                                </div>
                            )}

                            {/* SUBJECT */}
                            <div className="mb-3">
                                <label className="form-label mb-1">Subject</label>
                                <Field
                                    type="text"
                                    name="subject"
                                    placeholder="Email subject"
                                    className="input"
                                />
                                <div className="flex items-center justify-between mt-1 text-xs text-gray-500">
                                    <span>
                                        {(values.subject?.length || 0)}/{200} characters
                                    </span>
                                    <ErrorMessage
                                        name="subject"
                                        component="div"
                                        className="mt-1 text-xs text-red-500"
                                    />
                                </div>
                            </div>

                            {/* MESSAGE */}
                            <div className="mb-3">
                                <label className="form-label mb-1">Message</label>
                                <Field
                                    as="textarea"
                                    rows="5"
                                    name="email_body"
                                    placeholder="Write your message..."
                                    className="input pt-2 h-[150px]"
                                />
                                <div className="flex items-center justify-between mt-1 text-xs text-gray-500">
                                    <span>
                                        {(values.email_body?.length || 0)}/{1000} characters
                                    </span>
                                    <ErrorMessage
                                        name="email_body"
                                        component="div"
                                        className="mt-1 text-xs text-red-500"
                                    />
                                </div>
                            </div>

                            {showInvoiceTemplate &&
                                <div className="mb-4">
                                    <div className="flex items-center my-5">
                                        <span className="w-1/2 h-0.5 border border-dashed inline-block"></span>
                                        <p className="text-sm shrink-0 w-fit px-4">Choose Email Template</p>
                                        <span className="w-1/2 h-0.5 border border-dashed inline-block"></span>
                                    </div>

                                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {emailTemplates.map((item) => (
                                            <div key={item.id} className="invoice-template-wrapper group">
                                                <label
                                                    className="
                                                    cursor-pointer invoice-template-preview
                                                    flex items-end border bg-no-repeat bg-cover bg-top
                                                    border-gray-300 dark:border-gray-200 rounded-xl
                                                    has-[:checked]:!border-success has-[:checked]:border-2 [&_.checked]:has-[:checked]:flex
                                                    h-[210px] mb-1
                                                "
                                                    style={{
                                                        backgroundImage: `url(${toAbsoluteUrl(`/media/illustrations/${item.image}`)})`,
                                                    }}
                                                >
                                                    <input
                                                        type="radio"
                                                        name="showInvoiceTemplate"
                                                        value={item.id}
                                                        checked={Number(values.showInvoiceTemplate) === item.id}
                                                        onChange={() => setFieldValue("showInvoiceTemplate", item.id)}
                                                        className="appearance-none"
                                                    />
                                                    <KeenIcon
                                                        icon="check-circle"
                                                        className="checked ms-5 mb-5 text-xl text-success leading-none hidden"
                                                        style="solid"
                                                    />
                                                </label>

                                                <div className="flex items-center justify-between mt-1.5 gap-2">
                                                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                                                        {item.label}
                                                    </span>
                                                    <a
                                                        href={toAbsoluteUrl(`/media/illustrations/${item.image}`)}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="btn-link !leading-3 shrink-0"
                                                    >
                                                        Preview
                                                    </a>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            }

                            {/* BUTTONS */}
                            <div className="flex items-center justify-end gap-2 mt-4">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="btn btn-sm btn-primary"
                                >
                                    {loading ? "Please Wait..." : "Send"}
                                </button>

                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="btn btn-sm btn-outline btn-danger"
                                >
                                    Cancel
                                </button>
                            </div>

                        </Form>
                    )}
                </Formik>

            </div>
        </div>
    );
};

export { EmailSendModal };
